import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { Loader2, DollarSign, ShieldCheck, RefreshCw, History, Lock } from "lucide-react";

// v1.0.0 Item 8 — Two-step paid sweep budget activation UI.
// Step 1: admin inputs amount → POST /admin/paid-sweep/budget/request → returns request_id
// Step 2: admin confirms SAME amount → POST /admin/paid-sweep/budget/confirm → activates
// Single-step reset back to $0 via /budget/reset.

export default function BudgetActivationControl({ onChange }) {
  const [state, setState] = useState(null);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState(0);
  const [confirmAmount, setConfirmAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/paid-sweep/budget`);
      setState(res.data.current || null);
      setPending(res.data.pending_request || null);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submitRequest = async () => {
    if (amount < 0) { toast.error("Amount must be >= 0"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/paid-sweep/budget/request`, { budget_cap: Number(amount) });
      toast.info(`Step 1 complete. Confirm same amount within 10 minutes.`);
      setPending({ request_id: res.data.request_id, requested_budget_cap: res.data.requested_budget_cap });
      setConfirmAmount(0);
      if (onChange) onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed");
    } finally { setSubmitting(false); }
  };

  const submitConfirm = async () => {
    if (!pending) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/paid-sweep/budget/confirm`, {
        request_id: pending.request_id,
        budget_cap: Number(confirmAmount),
      });
      toast.success(`Budget activated: $${Number(confirmAmount).toFixed(2)}`);
      setPending(null);
      setAmount(0);
      setConfirmAmount(0);
      await load();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Confirm failed");
    } finally { setSubmitting(false); }
  };

  const submitReset = async () => {
    if (!window.confirm("Reset paid sweep budget to $0 and deactivate? This stops all paid sweep activation.")) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/paid-sweep/budget/reset`);
      toast.success("Budget reset to $0");
      await load();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setSubmitting(false); }
  };

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/paid-sweep/budget/history`);
      setHistory(res.data.items || []);
    } catch { setHistory([]); }
  };

  if (loading) return <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>;

  const isActive = state?.is_active === true;
  const currentCap = Number(state?.budget_cap || 0);

  return (
    <div className="space-y-3 bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid="budget-activation-control">
      <div className="flex items-center gap-2">
        <Lock className={`w-3.5 h-3.5 ${isActive ? "text-emerald-500" : "text-slate-400"}`} />
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-600">Paid Sweep Budget Activation</p>
      </div>

      {/* Current state */}
      <div className="flex items-center gap-3 px-3 py-2 bg-white rounded-lg border">
        <DollarSign className={`w-4 h-4 ${isActive ? "text-emerald-500" : "text-slate-400"}`} />
        <div className="flex-1">
          <p className="text-xs font-semibold text-slate-800">
            ${currentCap.toFixed(2)}{" "}
            <span className={`text-[10px] font-bold ${isActive ? "text-emerald-600" : "text-slate-400"}`}>
              {isActive ? "ACTIVE" : "INACTIVE"}
            </span>
          </p>
          <p className="text-[10px] text-slate-400">
            {state?.last_changed_by_email || "never set"} · {state?.last_changed_at?.slice(0, 19)}
          </p>
        </div>
        {isActive && (
          <button
            onClick={submitReset}
            disabled={submitting}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
            data-testid="budget-reset-btn"
          >
            <RefreshCw className="w-3 h-3" /> Reset to $0
          </button>
        )}
      </div>

      {/* Step 1: Request */}
      {!pending && (
        <div className="space-y-2">
          <p className="text-[10px] text-slate-500 leading-tight">
            <strong>Two-step activation.</strong> Step 1: enter amount. Step 2: re-enter same amount to confirm within 10 minutes.
          </p>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white border rounded-lg px-2 py-1 flex-1">
              <DollarSign className="w-3 h-3 text-slate-400" />
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                className="flex-1 text-xs bg-transparent outline-none"
                min="0"
                step="10"
                placeholder="0.00"
                data-testid="budget-request-input"
              />
            </div>
            <button
              onClick={submitRequest}
              disabled={submitting || amount <= 0}
              className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-[11px] font-bold hover:bg-amber-600 disabled:opacity-50"
              data-testid="budget-request-btn"
            >
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : "Step 1 — Request"}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Confirm */}
      {pending && (
        <div className="space-y-2 p-2 rounded-lg bg-amber-50 border-2 border-amber-400">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
            <p className="text-[11px] font-bold text-amber-900">
              Pending: ${Number(pending.requested_budget_cap).toFixed(2)}. Re-enter to confirm.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white border rounded-lg px-2 py-1 flex-1">
              <DollarSign className="w-3 h-3 text-slate-400" />
              <input
                type="number"
                value={confirmAmount}
                onChange={(e) => setConfirmAmount(parseFloat(e.target.value) || 0)}
                className="flex-1 text-xs bg-transparent outline-none"
                min="0"
                step="10"
                placeholder="Re-enter same amount"
                data-testid="budget-confirm-input"
              />
            </div>
            <button
              onClick={submitConfirm}
              disabled={submitting || confirmAmount <= 0}
              className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-700 disabled:opacity-50"
              data-testid="budget-confirm-btn"
            >
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : "Step 2 — Confirm"}
            </button>
            <button
              onClick={() => { setPending(null); setConfirmAmount(0); setAmount(0); }}
              className="px-2 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-[11px] font-bold hover:bg-slate-200"
              data-testid="budget-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* History toggle */}
      <button
        onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }}
        className="w-full flex items-center justify-center gap-1 py-1 text-[10px] font-semibold text-slate-500 hover:text-slate-700"
        data-testid="budget-history-toggle"
      >
        <History className="w-3 h-3" /> {showHistory ? "Hide" : "Show"} audit history
      </button>
      {showHistory && (
        <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="budget-history-list">
          {history.length === 0 ? (
            <p className="text-[10px] text-slate-400 text-center py-2">No history yet</p>
          ) : history.map((h, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1 bg-white rounded text-[10px]">
              <span className={`px-1.5 py-0.5 rounded font-bold text-[9px] ${h.action === "activated" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}>
                {h.action}
              </span>
              <span className="font-bold text-slate-700">${Number(h.new_budget_cap || 0).toFixed(2)}</span>
              <span className="text-slate-400">by {h.changed_by_email}</span>
              <span className="ml-auto text-slate-400">{(h.changed_at || "").slice(0, 16)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
